import React from 'react';
import { MapPin, Navigation, Anchor } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LocationPage = () => {
  const { t, language } = useLanguage();

  const nearbyPlaces = [
    {
      id: 'kiss-bike-pordelio',
      image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_adriatic-escape-3/artifacts/5tphcwmd_WhatsApp%20Image%202026-07-23%20at%2012.05.06%20%281%29.jpeg',
      title: { it: 'Kiss Bike Pordelio', en: 'Kiss Bike Pordelio', de: 'Kiss Bike Pordelio' },
      description: {
        it: 'L\'iconica installazione a forma di cuore sulla Via Pordelio, tappa fotografica lungo la ciclabile panoramica che costeggia la laguna nord.',
        en: 'The iconic heart-shaped installation on Via Pordelio, a photo stop along the scenic cycling path bordering the northern lagoon.',
        de: 'Die ikonische herzförmige Installation an der Via Pordelio, ein Fotostopp entlang des malerischen Radwegs an der Nordlagune.'
      }
    },
    {
      id: 'laguna-sunset',
      image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_adriatic-escape-3/artifacts/9pfq1so3_WhatsApp%20Image%202026-07-23%20at%2012.04.04.webp',
      title: { it: 'Tramonto in Laguna', en: 'Sunset over the Lagoon', de: 'Sonnenuntergang in der Lagune' },
      description: {
        it: 'Le barene e i canneti si specchiano nelle acque calme al tramonto, con la silhouette delle Dolomiti sullo sfondo.',
        en: 'Salt marshes and reeds reflect on the still waters at sunset, with the Dolomites silhouetted in the background.',
        de: 'Salzwiesen und Schilf spiegeln sich bei Sonnenuntergang im ruhigen Wasser, mit den Dolomiten als Silhouette im Hintergrund.'
      }
    },
    {
      id: 'lagoon-boat',
      image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_adriatic-escape-3/artifacts/qfx8cbdv_WhatsApp%20Image%202026-07-23%20at%2012.04.04%20%284%29.jpeg',
      title: { it: 'Barchino al Pontile', en: 'Boat at the Pier', de: 'Boot am Steg' },
      description: {
        it: 'La quiete di un pontile lagunare al calar del sole, dove ancora vivono i ritmi della pesca tradizionale veneta.',
        en: 'The tranquility of a lagoon pier at sundown, where the rhythms of traditional Venetian fishing still endure.',
        de: 'Die Ruhe eines Lagunenstegs bei Sonnenuntergang, wo der Rhythmus der traditionellen venezianischen Fischerei noch lebt.'
      }
    },
    {
      id: 'lagoon-promenade',
      image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_adriatic-escape-3/artifacts/xtbh51sj_WhatsApp%20Image%202026-07-23%20at%2012.05.07.jpeg',
      title: { it: 'Passeggiata Lungolaguna', en: 'Lagoon Promenade', de: 'Lagunenpromenade' },
      description: {
        it: 'Il waterfront della laguna nord, ideale per passeggiate romantiche al tramonto tra riflessi dorati e frescura del mare.',
        en: 'The northern lagoon waterfront, perfect for romantic sunset strolls amid golden reflections and sea breezes.',
        de: 'Die Uferpromenade der Nordlagune, ideal für romantische Spaziergänge bei Sonnenuntergang mit goldenen Reflexen und Meeresbrise.'
      }
    },
    {
      id: 'faro-piave-vecchia',
      image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_adriatic-escape-3/artifacts/jw74uadp_WhatsApp%20Image%202026-07-23%20at%2012.07.26%20%282%29.jpeg',
      title: { it: 'Faro di Piave Vecchia', en: 'Piave Vecchia Lighthouse', de: 'Leuchtturm Piave Vecchia' },
      description: {
        it: 'Il simbolo di Cavallino Treporti: il faro a strisce bianche e nere che dal 1950 illumina la costa. Perfetto per lunghe passeggiate sulla spiaggia dorata.',
        en: 'The symbol of Cavallino Treporti: the black-and-white striped lighthouse illuminating the coast since 1950. Perfect for long walks on the golden beach.',
        de: 'Das Wahrzeichen von Cavallino Treporti: der schwarz-weiß gestreifte Leuchtturm, der die Küste seit 1950 beleuchtet. Ideal für lange Spaziergänge am goldenen Strand.'
      }
    }
  ];

  const distances = [
    { name: { it: 'Spiagge di Cavallino', en: 'Cavallino Beaches', de: 'Cavallino Strände' }, distance: '200m', time: { it: '3 min a piedi', en: '3 min walk', de: '3 Min. zu Fuß' }, icon: Anchor },
    { name: { it: 'Venezia (vaporetto)', en: 'Venice (ferry)', de: 'Venedig (Fähre)' }, distance: '15km', time: { it: '20 min', en: '20 min', de: '20 Min.' }, icon: Navigation },
    { name: { it: 'Jesolo', en: 'Jesolo', de: 'Jesolo' }, distance: '8km', time: { it: '10 min in auto', en: '10 min by car', de: '10 Min. mit dem Auto' }, icon: Navigation },
    { name: { it: 'Aeroporto Marco Polo', en: 'Marco Polo Airport', de: 'Flughafen Marco Polo' }, distance: '25km', time: { it: '25 min in auto', en: '25 min by car', de: '25 Min. mit dem Auto' }, icon: Navigation }
  ];

  const attractions = [
    { name: 'Piazza San Marco', category: { it: 'Cultura', en: 'Culture', de: 'Kultur' } },
    { name: 'Ponte di Rialto', category: { it: 'Cultura', en: 'Culture', de: 'Kultur' } },
    { name: 'Murano e Burano', category: { it: 'Escursioni', en: 'Tours', de: 'Ausflüge' } },
    { name: { it: 'Parco Naturale Laguna', en: 'Lagoon Natural Park', de: 'Naturpark Lagune' }, category: { it: 'Natura', en: 'Nature', de: 'Natur' } },
    { name: 'Lido di Venezia', category: { it: 'Spiaggia', en: 'Beach', de: 'Strand' } },
    { name: 'Caorle', category: { it: 'Escursioni', en: 'Tours', de: 'Ausflüge' } }
  ];

  const services = [
    { name: { it: 'Supermercati', en: 'Supermarkets', de: 'Supermärkte' }, category: 'Shopping' },
    { name: { it: 'Ristoranti', en: 'Restaurants', de: 'Restaurants' }, category: { it: 'Ristorazione', en: 'Dining', de: 'Gastronomie' } },
    { name: { it: 'Farmacia', en: 'Pharmacy', de: 'Apotheke' }, category: { it: 'Salute', en: 'Health', de: 'Gesundheit' } },
    { name: { it: 'Noleggio Bici', en: 'Bike Rental', de: 'Fahrradverleih' }, category: { it: 'Servizi', en: 'Services', de: 'Dienste' } },
    { name: { it: 'Stabilimenti Balneari', en: 'Beach Facilities', de: 'Strandeinrichtungen' }, category: { it: 'Mare', en: 'Sea', de: 'Meer' } },
    { name: { it: 'Escursioni in Laguna', en: 'Lagoon Tours', de: 'Lagunentouren' }, category: { it: 'Attività', en: 'Activities', de: 'Aktivitäten' } }
  ];

  const localize = (val) => {
    if (typeof val === 'string') return val;
    return val[language] || val.it;
  };

  return (
    <div data-testid="location-page" className="min-h-screen pt-20 bg-white">
      <section className="bg-[#F9F6F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">{t('location.overline')}</p>
          <h1 data-testid="location-title" className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-[#1A1A1A] mb-4">
            {t('location.title')}
          </h1>
          <p className="text-base text-[#666666] max-w-2xl mx-auto leading-relaxed">{t('location.subtitle')}</p>
        </div>
      </section>

      {/* OpenStreetMap Embed */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg overflow-hidden border border-[#2C5263]/10 shadow-sm">
            <iframe
              data-testid="location-map"
              title="Cavallino Treporti Map"
              width="100%"
              height="450"
              frameBorder="0"
              scrolling="no"
              marginHeight="0"
              marginWidth="0"
              src="https://www.openstreetmap.org/export/embed.html?bbox=12.4200%2C45.4200%2C12.5500%2C45.4900&amp;layer=mapnik&amp;marker=45.4530%2C12.4830"
              style={{ border: 0 }}
            />
          </div>
          <div className="text-center mt-4">
            <a
              href="https://www.openstreetmap.org/?mlat=45.4530&mlon=12.4830#map=13/45.4530/12.4830"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-map-link"
              className="text-[#2C5263] hover:text-[#1A3644] font-medium text-sm"
            >
              {language === 'de' ? 'In größerer Karte anzeigen →' : language === 'en' ? 'View larger map →' : 'Visualizza mappa più grande →'}
            </a>
          </div>
        </div>
      </section>

      {/* Distances */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-[#1A1A1A] mb-12 text-center">{t('location.distancesTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {distances.map((item) => {
              const Icon = item.icon;
              const name = localize(item.name);
              return (
                <div
                  key={`distance-${item.distance}-${typeof item.name === 'string' ? item.name : item.name.it}`}
                  data-testid={`distance-${typeof item.name === 'string' ? item.name : item.name.it}`}
                  className="bg-[#F9F6F0] p-6 rounded-lg border border-[#2C5263]/10 text-center"
                >
                  <Icon size={32} className="text-[#2C5263] mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{name}</h3>
                  <p className="text-[#666666] text-sm mb-1">{item.distance}</p>
                  <p className="text-[#2C5263] text-xs font-medium">{localize(item.time)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Attractions */}
      <section className="py-16 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-[#1A1A1A] mb-12 text-center">{t('location.attractionsTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {attractions.map((attraction) => (
              <div
                key={typeof attraction.name === 'string' ? attraction.name : attraction.name.it}
                data-testid={`attraction-${typeof attraction.name === 'string' ? attraction.name : attraction.name.it}`}
                className="bg-white p-4 rounded-lg border border-[#2C5263]/10 flex items-center justify-between"
              >
                <span className="text-[#1A1A1A] font-medium">{localize(attraction.name)}</span>
                <span className="text-xs text-[#666666] bg-[#F9F6F0] px-3 py-1 rounded">{localize(attraction.category)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Gallery - Da vedere nelle vicinanze */}
      <section data-testid="nearby-gallery-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">{t('location.nearbyGalleryOverline')}</p>
            <h2 className="text-3xl sm:text-4xl font-serif text-[#1A1A1A] mb-4">{t('location.nearbyGalleryTitle')}</h2>
            <p className="text-base text-[#666666] max-w-2xl mx-auto leading-relaxed">{t('location.nearbyGallerySubtitle')}</p>
          </div>

          {/* Featured large card + grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Featured (first item, larger) */}
            <div
              data-testid={`nearby-place-${nearbyPlaces[0].id}`}
              className="lg:col-span-7 group relative overflow-hidden rounded-lg border border-[#2C5263]/10 shadow-sm"
            >
              <div className="relative h-80 lg:h-full overflow-hidden">
                <img
                  src={nearbyPlaces[0].image}
                  alt={localize(nearbyPlaces[0].title)}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                  <div className="inline-flex items-center gap-1 text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase mb-2">
                    <MapPin size={12} /> Cavallino Treporti
                  </div>
                  <h3 className="text-2xl md:text-3xl font-serif mb-2">{localize(nearbyPlaces[0].title)}</h3>
                  <p className="text-sm text-white/85 max-w-xl leading-relaxed">{localize(nearbyPlaces[0].description)}</p>
                </div>
              </div>
            </div>

            {/* Right column - 2 stacked */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
              {nearbyPlaces.slice(1, 3).map((place) => (
                <div
                  key={place.id}
                  data-testid={`nearby-place-${place.id}`}
                  className="group relative overflow-hidden rounded-lg border border-[#2C5263]/10 shadow-sm"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={place.image}
                      alt={localize(place.title)}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/10 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                      <h3 className="text-lg md:text-xl font-serif mb-1">{localize(place.title)}</h3>
                      <p className="text-xs text-white/85 line-clamp-2 leading-relaxed">{localize(place.description)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom row - 2 wide */}
            {nearbyPlaces.slice(3).map((place) => (
              <div
                key={place.id}
                data-testid={`nearby-place-${place.id}`}
                className="lg:col-span-6 group relative overflow-hidden rounded-lg border border-[#2C5263]/10 shadow-sm"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={place.image}
                    alt={localize(place.title)}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-xl md:text-2xl font-serif mb-1">{localize(place.title)}</h3>
                    <p className="text-sm text-white/85 max-w-md leading-relaxed">{localize(place.description)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-[#1A1A1A] mb-12 text-center">{t('location.servicesTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <div
                key={typeof service.name === 'string' ? service.name : service.name.it}
                data-testid={`service-${typeof service.name === 'string' ? service.name : service.name.it}`}
                className="bg-[#F9F6F0] p-4 rounded-lg border border-[#2C5263]/10 flex items-center justify-between"
              >
                <span className="text-[#1A1A1A] font-medium">{localize(service.name)}</span>
                <span className="text-xs text-[#666666] bg-white px-3 py-1 rounded">{localize(service.category)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LocationPage;
